import {
  AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, OnInit, Signal, signal, viewChild, WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { EventLoggerService } from '@services/event-logger-service';
import { OnboardingProfileValidationService } from '@services/onboarding/onboarding-profile-validation.service';
import { UserService } from '@services/user';
import { GenderFormErrorMessages, GenderFormErrorMessagesType } from '../../../../../e2e/src/shared/constants';
import {
  ONBOARDING_CONCERN_QUERY_PARAMS, ONBOARDING_CONCERN_ROUTE, ONBOARDING_PROFILE_ROUTES,
} from '../onboarding-profile.constants';
import { OnboardingProfileFieldErrorComponent } from '../onboarding-profile-field-error/onboarding-profile-field-error.component';
import { OnboardingProfileShellComponent } from '../onboarding-profile-shell/onboarding-profile-shell.component';

/** Age is two digits; the input is capped so the numeric keypad cannot overflow it. */
const MAX_AGE_DIGITS = 2;

/** Step 3 of the split profile flow. Never reached under `skip_age_page` — the name page routes past it. */
@Component({
  selector: 'app-onboarding-profile-age',
  templateUrl: './onboarding-profile-age.component.html',
  imports: [OnboardingProfileShellComponent, OnboardingProfileFieldErrorComponent, ButtonCtaComponent, FormsModule],
})
export class OnboardingProfileAgeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly router: Router = inject(Router);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly validation: OnboardingProfileValidationService = inject(OnboardingProfileValidationService);
  private readonly userService: UserService = inject(UserService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  protected readonly commonUtilityHelper: CommonUtilityHelper = inject(CommonUtilityHelper);

  private readonly ageInput: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild('ageInput');

  protected readonly age: WritableSignal<string> = signal('');
  protected readonly loading: WritableSignal<boolean> = signal(false);
  protected readonly errorMessage: WritableSignal<string> = signal('');
  protected readonly firstName: WritableSignal<string> = signal('');
  protected readonly MAX_AGE_DIGITS: number = MAX_AGE_DIGITS;
  protected readonly GenderFormErrorMessages: GenderFormErrorMessagesType = GenderFormErrorMessages;

  protected readonly hasError: Signal<boolean> = computed((): boolean => !!this.errorMessage());

  protected readonly isFilled: Signal<boolean> = computed((): boolean => this.age().length > 0);

  protected readonly isValidAndFilled: Signal<boolean> = computed(
    (): boolean => this.isFilled() && !this.hasError(),
  );

  protected readonly isDravidianScriptUser: Signal<boolean> = computed(
    (): boolean => this.userService.dravidianScripts.includes(this.conn.getActingUser()?.get('languagePreference')),
  );

  ngOnInit(): void {
    const user = this.conn.getActingUser();
    this.age.set(user?.get('Age') || '');
    this.firstName.set(user?.get('PatientName')?.split(' ')?.[0] || '');
    this.currentComponent.set(this);
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'onboarding-profile-age' }));
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
  }

  /** Autofocus so the number pad is already up when the page lands. */
  ngAfterViewInit(): void {
    this.ageInput()?.nativeElement.focus();
  }

  protected onAgeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = this.validation.sanitizeAge(input.value);
    this.age.set(input.value);
    if (this.hasError()) this.errorMessage.set('');
  }

  protected onInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  protected async handleContinue(): Promise<void> {
    if (this.loading()) return;
    const error = this.validation.validateAge(this.age());
    if (error) {
      this.errorMessage.set(error);
      this.ageInput()?.nativeElement.focus();
      this.eventLogger.trackEvent('age_validation_failed', { age: Number(this.age()) });
      return;
    }
    this.errorMessage.set('');
    this.loading.set(true);
    try {
      await this.conn.updateUserData({ Age: this.age() });
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: String(error) });
      return;
    } finally {
      this.loading.set(false);
    }
    await this.router.navigate([ONBOARDING_CONCERN_ROUTE], { queryParams: ONBOARDING_CONCERN_QUERY_PARAMS });
  }

  protected async handleBack(): Promise<void> {
    await this.router.navigate([ONBOARDING_PROFILE_ROUTES.NAME]);
  }

  async handleBackPress(): Promise<boolean> {
    await this.handleBack();
    return true;
  }
}
